import { useEffect, useRef } from 'react';
import type { CrmDataSlice } from '../lib/crmDataRefresh';
import {
  getCrmDbSchema,
  getDbSupabase,
  isSupabaseConfigured,
  supabase,
  type DbPortalKind,
} from '../lib/supabase';

/** Coalesce bursts (e.g. transfer apply updates many rows) into one quiet refresh. */
const DEBOUNCE_MS = 600;

const CRM_REALTIME_TABLES: { table: string; slice: CrmDataSlice }[] = [
  { table: 'projects', slice: 'projects' },
  { table: 'clients', slice: 'clients' },
  { table: 'contacts', slice: 'contacts' },
  { table: 'tasks', slice: 'tasks' },
  { table: 'task_attachments', slice: 'tasks' },
  { table: 'project_updates', slice: 'updates' },
  { table: 'account_transfer_requests', slice: 'transferRequests' },
  { table: 'account_meeting_minutes', slice: 'meetingMinutes' },
  { table: 'account_activity_log', slice: 'activityLogs' },
];

type UseCrmDataRealtimeOptions = {
  enabled?: boolean;
  portalKind?: DbPortalKind;
  onChange?: (slices: CrmDataSlice[]) => void;
};

/**
 * Live-refresh CRM data when shared tables change.
 * Only re-fetches the slice(s) for tables that actually changed.
 * Team list (`users`) listens on access_management.portal_access.
 */
export function useCrmDataRealtime(options: UseCrmDataRealtimeOptions = {}) {
  const { enabled = true, portalKind = 'crm', onChange } = options;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const timerRef = useRef<number | null>(null);
  const pendingSlicesRef = useRef<Set<CrmDataSlice>>(new Set());

  useEffect(() => {
    if (!enabled || !isSupabaseConfigured) return undefined;
    if (portalKind !== 'crm' && portalKind !== 'it') return undefined;

    const flush = () => {
      if (pendingSlicesRef.current.size === 0) return;
      const slices = [...pendingSlicesRef.current];
      pendingSlicesRef.current.clear();
      onChangeRef.current?.(slices);
    };

    const schedule = (slice: CrmDataSlice) => {
      pendingSlicesRef.current.add(slice);
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(flush, DEBOUNCE_MS);
    };

    const schema = getCrmDbSchema(portalKind);
    const db = getDbSupabase(portalKind);
    let channel = db.channel(`crm-data-live:${portalKind}`);
    for (const { table, slice } of CRM_REALTIME_TABLES) {
      channel = channel.on('postgres_changes', { event: '*', schema, table }, () => schedule(slice));
    }
    channel.subscribe();

    const accessChannels: ReturnType<NonNullable<typeof supabase>['channel']>[] = [];
    if (supabase) {
      const access = supabase
        .channel(`crm-data-live:${portalKind}:access`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'access_management', table: 'portal_access' },
          () => schedule('users'),
        );
      access.subscribe();
      accessChannels.push(access);
    }

    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      pendingSlicesRef.current.clear();
      void db.removeChannel(channel);
      for (const ch of accessChannels) {
        void ch.unsubscribe();
      }
    };
  }, [enabled, portalKind]);
}
